import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import BoardForm, {
  BoardFormProps,
} from '@/components/Board/CreateBoardModal/BoardForm/index';
import useBoardFormAction, {
  useBoardFormActionProps,
} from '@/hooks/board/useBoardFormAction';
import useInterval from '@/hooks/useInterval';
import { BoardFormRef } from '@/components/Board/CreateBoardModal';
import useBoardForm, { defaultValues } from '@/hooks/board/useBoardForm';
import FormProvider from '@/components/HookForm/FormProvider';
import { stripHTML } from '@/utils';
import { useGetTempBoard } from '@/hooks/apiHooks';
import { getDateFormat } from '@/utils/format';
import { useDialog } from '@/hooks/global-tools';
import { useWatch } from 'react-hook-form';

type NewBoardFormProps = {
  defaultCategory?: string;
  isFormDirty: boolean;
  isDeploying?: boolean;
  setIsFormDirty: (isFormDirty: boolean) => void;
  setDisabledSubmit: (value: boolean) => void;
  setDisabledSave: (value: boolean) => void;
  setDescription: (value: string) => void;
  setFormType: (formType: 'editor' | 'codeUpload') => void;
  handleClose: () => void;
} & Pick<useBoardFormActionProps, 'onSubmit'> &
  Pick<BoardFormProps, 'options'>;

const NewBoardForm = forwardRef<BoardFormRef, NewBoardFormProps>(
  (props, ref) => {
    const {
      defaultCategory,
      isFormDirty,
      isDeploying = false,
      setIsFormDirty,
      setDisabledSubmit,
      setDisabledSave,
      setDescription,
      setFormType,
      options,
      onSubmit,
      handleClose,
    } = props;

    const dialog = useDialog();

    const methods = useBoardForm();
    const {
      reset,
      control,
      getValues,
      setValue,
      formState: { isSubmitting, isValid, isSubmitSuccessful, dirtyFields },
    } = methods;
    const watchedSubject = useWatch({ control, name: 'subject' });
    const watchedCategories = useWatch({ control, name: 'categories' });
    const watchedContent = useWatch({ control, name: 'content' });
    const watchedFormType = useWatch({ control, name: 'formType' });

    const isInit = useRef(true);
    const {
      data: tempBoardList,
      isLoading: isTempBoardListLoading,
      isValidating: isTempBoardListValidating,
      mutate: mutateTempBoardList,
    } = useGetTempBoard();
    const action = useBoardFormAction({
      methods,
      onSubmit,
    });
    const {
      documentId,
      setDocumentId,
      isGetEditDetailBoardLoading,
      saveTime,
      deleteTempData,
      save,
    } = action;

    const isLoading =
      isTempBoardListLoading ||
      isTempBoardListValidating ||
      isGetEditDetailBoardLoading;

    useEffect(() => {
      if (!isInit.current || isLoading) {
        return;
      }
      isInit.current = false;

      if (Array.isArray(tempBoardList) && tempBoardList.length > 0) {
        tempBoardList.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

        const formData = tempBoardList[0];
        if (formData) {
          dialog.show({
            message: `${getDateFormat(
              formData.updatedAt,
            )}에\n쓰고있던 글이 있어요.\n이어서 쓸까요?`,
            onConfirm: () => {
              setDocumentId(formData.id);
            },
            isBlockBackDropClose: true,
            showCloseButton: false,
            onCancel: () => {
              deleteTempData(tempBoardList.map((item) => item.id)).then(
                mutateTempBoardList,
              );
            },
          });
        }
      }
    }, [isTempBoardListLoading, isTempBoardListValidating]);

    // 게시물 작성 시 기본 카테고리 선택
    useEffect(() => {
      if (defaultCategory) setValue('categories', [defaultCategory]);
    }, [defaultCategory]);

    // form submit Disabled 처리
    useEffect(() => {
      // action 로딩 중, form invalid, form isSubmitting
      setDisabledSubmit(isLoading || !isValid || isSubmitting);

      // 임시저장 버튼은 기본 필드만 검증 (제목, 카테고리)
      const hasBasicFields =
        watchedSubject?.trim() && (watchedCategories?.length ?? 0) > 0;
      setDisabledSave(isLoading || isSubmitting || !hasBasicFields);
    }, [isLoading, isValid, isSubmitting, watchedSubject, watchedCategories]);

    // 자동 저장 시간 값 세팅
    useEffect(() => {
      if (!saveTime) return;
      setDescription(`${saveTime} 자동저장`);
    }, [saveTime]);

    // form에 값 수정될 경우 isFormDirty 값 변경
    useEffect(() => {
      setIsFormDirty(
        Boolean(
          Object.keys(dirtyFields).some(
            (fieldName: string) =>
              fieldName !== 'content' ||
              !!stripHTML(watchedContent ?? '').trim(),
          ),
        ),
      );
    }, [dirtyFields, watchedContent]);

    // formType 변경 시 부모에게 알림
    useEffect(() => {
      setFormType(watchedFormType);
    }, [watchedFormType]);

    // submit 완료 후 모달 닫기
    useEffect(() => {
      if (isSubmitSuccessful) handleClose();
    }, [isSubmitSuccessful]);

    useImperativeHandle(ref, () => ({
      action: {
        ...action,
        getCodeZipFileStatus: () => {
          const values = getValues();
          return values.codeZipFile?.status;
        },
        reset: () => {
          const resetValues = defaultValues;
          if (defaultCategory) resetValues.categories = [defaultCategory];
          reset(resetValues);
          isInit.current = true;
          mutateTempBoardList();
        },
        getValues,
        setValue,
      },
    }));

    // 자동 저장 interval
    useInterval(
      useCallback(async () => {
        if (!isDeploying && (isFormDirty || documentId)) {
          await save(false);
        }
      }, [save, isFormDirty, documentId, isDeploying]),
      60 * 1000,
    );

    return (
      <FormProvider methods={methods} id={'CreateBoardModal-New'}>
        <BoardForm isLoading={isLoading} options={options} methods={methods} />
      </FormProvider>
    );
  },
);
NewBoardForm.displayName = 'NewBoardForm';

export default NewBoardForm;
